import { FC, useEffect, useState } from 'react';
import { BookmarkFilledIcon, BookmarkIcon } from 'assets/icons';
import { CustomButton } from 'components';
import { TeamView } from 'containers/dispatch/dispatch.model';

interface ViewsPopoverProps {
  onClose: () => void;
  onOpenCreateViewModal: () => void;
  views: TeamView[];
  handleViewSelection: (viewIds: number[]) => void;
  hasCreateViewPermission: boolean;
  isTeamViewsLoading: boolean;
}

interface ViewOption extends TeamView {
  isSelected: boolean;
}
const uniqueSections: { type: string; label: string }[] = [
  { type: 'created', label: 'Created by me' },
  { type: 'shared', label: 'Shared' }
];

const ViewsPopover: FC<ViewsPopoverProps> = ({
  onOpenCreateViewModal,
  views,
  handleViewSelection,
  hasCreateViewPermission,
  isTeamViewsLoading
}) => {
  const [viewOptions, setViewOptions] = useState<ViewOption[]>([]);

  useEffect(() => {
    setViewOptions(() => views.map(view => ({ ...view, isSelected: view.isEnabled })));
  }, [views]);

  const getGroupedViewOptions = (section: string) => {
    const groupedViewOptions = viewOptions.filter(option => option.type.toLowerCase() === section.toLowerCase());
    return groupedViewOptions;
  };

  const getDefaultViewOptions = () => {
    const defaultViewOptions = viewOptions.filter(option => option.type.toLowerCase() === 'default');
    const defaultViews = [
      { name: 'All jobs', type: 'default', order: 4 },
      // eslint-disable-next-line quotes
      { name: "Today's jobs", type: 'default', order: 1 },
      { name: 'Unscheduled jobs', type: 'default', order: 3 },
      { name: 'Active jobs', type: 'default', order: 2 }
    ];

    return defaultViewOptions.sort((a, b) => {
      const orderMapA = defaultViews.find(view => view.name === a.name)?.order || 999;
      const orderMapB = defaultViews.find(view => view.name === b.name)?.order || 999;
      // First sort by order
      const orderComparison = orderMapA - orderMapB;
      // If orders are equal, use id as secondary sort to ensure consistent ordering
      return orderComparison === 0 ? a.id - b.id : orderComparison;
    });
  };

  const handleSelectView = (option: ViewOption) => {
    let currentViewIds: number[] = viewOptions
      .filter(view => view.isEnabled)
      .sort((a, b) => a.priority - b.priority)
      .map(view => view.id);

    if (option.isSelected) {
      currentViewIds = currentViewIds.filter(view => view !== option.id);
      setViewOptions(prev => prev.map(view => (view.id === option.id ? { ...view, isSelected: false } : view)));
    } else {
      currentViewIds = [...currentViewIds, option.id];
      setViewOptions(prev => prev.map(view => (view.id === option.id ? { ...view, isSelected: true } : view)));
    }
    handleViewSelection(currentViewIds);
  };

  return (
    <div className="flex flex-col">
      <div className="m-3.5 flex items-center justify-between border-b border-borderGray pb-3">
        <div className="text-sbase font-bold text-primaryText">Add view</div>
        {hasCreateViewPermission && (
          <CustomButton
            onClick={onOpenCreateViewModal}
            id="create-new-view-button"
            type={null}
            className="h-fit px-0 text-sbase font-semibold text-secondary hover:shadow-none active:shadow-none">
            + Create new
          </CustomButton>
        )}
      </div>

      <div className="mx-3.5">
        <div className="mb-2 text-sm font-medium text-gray-500">
          {getDefaultViewOptions().length > 0 ? 'Default' : null}
          <div>
            {getDefaultViewOptions().map(option => (
              <button
                key={option.id}
                disabled={isTeamViewsLoading}
                className={'flex w-full items-center gap-2 rounded-md py-1.5 hover:bg-gray-100'}
                onClick={() => handleSelectView(option)}>
                {option.isSelected ? <BookmarkFilledIcon /> : <BookmarkIcon />}
                <span className="text-sbase text-primaryText">{option.name}</span>
              </button>
            ))}
          </div>
        </div>

        {uniqueSections.map(section => (
          <div key={section.type} className="mb-2 text-sm font-medium text-gray-500">
            {getGroupedViewOptions(section.type).length > 0 ? section.label : null}
            <div>
              {getGroupedViewOptions(section.type).map(option => (
                <button
                  key={option.id}
                  disabled={isTeamViewsLoading}
                  className={'flex w-full items-center gap-2 rounded-md py-1.5 hover:bg-gray-100'}
                  onClick={() => handleSelectView(option)}>
                  {option.isSelected ? <BookmarkFilledIcon /> : <BookmarkIcon />}
                  <span className="text-sbase text-primaryText">{option.name}</span>
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ViewsPopover;
